import { infoToast } from 'app/utils/toast';
import type { AxiosInstance } from 'axios';

import {
  getRetryDelay,
  logRetryDecision,
  logTokenRefresh,
} from './retryPolicy';

type ErrorInterceptorOptions = {
  // Passed in rather than imported: app/utils/refreshToken imports base.js,
  // so importing it here would create a cycle that breaks module loading.
  refreshToken: () => Promise<string>;
  setAuthHeader: (token: string) => void;
};

// The response error handler shared by both axios instances (base.js and
// axiosConfig.ts): a toast on 403, one token refresh on `token_not_valid`, then
// the per-status retry rules from retryPolicy.ts. Each instance gets its own
// in-flight refresh and sets the new token on its own default headers.
export const createErrorInterceptor = (
  instance: AxiosInstance,
  { refreshToken, setAuthHeader }: ErrorInterceptorOptions
) => {
  let refreshingToken: Promise<string> | null = null;

  return async (error: any) => {
    const { config } = error;

    if (config && !config.url?.includes('users/refresh_token')) {
      if (error?.response?.status === 403) {
        infoToast('You are not allowed to access this page.');
      } else if (
        error?.response?.data?.code === 'token_not_valid' &&
        !config._retry
      ) {
        logTokenRefresh(error);
        config._retry = true;
        refreshingToken = refreshingToken ? refreshingToken : refreshToken();

        const token = await refreshingToken;
        refreshingToken = null;

        setAuthHeader(token);

        return instance(config);
      } else {
        // Per-status retry rules (GET/HEAD/OPTIONS only), see retryPolicy.ts
        const retryDelay = getRetryDelay(error);
        logRetryDecision(error, retryDelay);

        if (retryDelay !== null) {
          config._retryCount = (config._retryCount || 0) + 1;

          await new Promise(resolve => setTimeout(resolve, retryDelay));

          // Cancelled during the wait (unmount, filter change): hand back the
          // real error instead of replaying a request nobody is waiting for.
          if (config.signal?.aborted) {
            return Promise.reject(error);
          }

          return instance(config);
        }
      }
    }

    return Promise.reject(error);
  };
};
